// tslint:disable: max-line-length
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { fn2 } from '../../../core/formatter/formatNumber';
import { PieChartTooltip } from './pieChartTooltip';
import * as React from 'react';
import style from '../../../core/forms/style/WForm.less'

interface PieDiagramProps extends React.ClassAttributes<any> {
    layout?: string;
    data: any[];
    legendTitle: any[];
    unit: string[];
    containerSize?: any;
    hideLegend?: boolean;
    id?: string;
    fillColor?: string[];
    bgColor?: string;
    selectable?: any;
    selectedChartKey?: string;
    customStyle?: React.CSSProperties;
    customLegendStyle?: React.CSSProperties;
    hideLabelsFilter?: (data: any, rows: any[], index: number) => boolean;
    onSelectChange?: (key: string) => void;
    getChartSelector?: (element: JSX.Element) => void;
    setTitle?: (elem?: JSX.Element) => JSX.Element;
    elemSelectPostition?: string;
}

interface PieDiagramState extends React.ClassAttributes<any> {
    selected: string;
}

export class PieDiagram extends React.Component<PieDiagramProps, PieDiagramState> {

    constructor(props: any) {
        super(props);
        this.state = {
            selected: typeof props.selectedChartKey === 'string' ? props.selectedChartKey : undefined
        };
        this.legendFormatter = this.legendFormatter.bind(this);
    }

    legendFormatter(value: any, entry: any): JSX.Element {
        const idx = this.props.data.findIndex((e: any) => e.name === value);
        return (
            <span>{this.props.legendTitle[idx]}</span>
        );
    }

    render(): JSX.Element {
        const { layout, containerSize, fillColor, data, selectable, getChartSelector, hideLabelsFilter, setTitle, elemSelectPostition } = this.props;

        if (!data) {
            return null;
        }
        const width = containerSize && containerSize.width ? containerSize.width : 600;
        const height = containerSize && containerSize.height ? containerSize.height : '100%';

        // Layout
        let aspect;
        let pieChartStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column-reverse' };
        let pieChartLegendStyle: React.CSSProperties = {
            fontSize: 12, lineHeight: '14px', color: '#555', height: 30, top: 0, position: 'relative'
        };
        let labelTextStyle: React.CSSProperties = { fontWeight: 'bold' };
        let pieChartOuterRadius = 100;

        if (selectable) {
            pieChartLegendStyle.maxWidth = '90%';
        }

        // console.log('pie custom style', this.props.customStyle);

        if (layout === 'row') {
            aspect = 1; /* !importent */
            pieChartStyle = {
                ...pieChartStyle,
                ...{ flexDirection: 'row', marginTop: -230 /* !importent */ },
                ...(this.props.customStyle || {})
            };
            pieChartLegendStyle = {
                ...pieChartLegendStyle,
                ...{ top: 260 /* !importent */, maxWidth: '40%' /* !importent */, height: '35%', overflowY: 'auto' },
                ...(this.props.customLegendStyle || {})
            };
            labelTextStyle = { ...labelTextStyle, ...{ fontSize: '1.2em' } }
            pieChartOuterRadius = 180;
        }

        // // Daten sortieren, größer zuerst.
        // data.sort((a: any, b: any) => {
        //     return a.value - b.value;
        // });

        // const showLabelData = data.filter((r: any, i: number) => typeof hideLabelsFilter === 'function' ? !hideLabelsFilter(r, data, i) : true);
        // const showLabelNum = showLabelData.length;

        const RADIAN = Math.PI / 180;
        const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: any) => {
            // Null-Label nicht anzeigen
            if (data[index].value === 0) {
                return null;
            }
            // Labels ausblenden durch Filter
            if (typeof hideLabelsFilter === 'function' && hideLabelsFilter(data[index], data, index)) {
                return null;
            }

            // const showLabelIndex = showLabelData.findIndex((r: any) => r.key === data[index].key);
            // const gap = (outerRadius - innerRadius) / (showLabelNum + 2);
            // const radius = innerRadius + (showLabelIndex + 1) * gap;
            // const x = (cx + radius * Math.cos(-midAngle * RADIAN)) * 0.95;
            // const y = (cy + radius * Math.sin(-midAngle * RADIAN)) * 0.95;

            const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
            const x = cx + radius * Math.cos(-midAngle * RADIAN);
            const y = cy + radius * Math.sin(-midAngle * RADIAN);

            const label = data[index].labelFormat
                ? data[index].labelFormat
                    .replace('[key]', data[index].key)
                    .replace('[name]', data[index].name)
                    .replace('[value]', fn2(data[index].value))
                    .replace('[percentage]', fn2(data[index].percentage))
                : data[index].name;
            return (
                <text
                    style={labelTextStyle}
                    x={x}
                    y={y}
                    fill='black'
                    dominantBaseline='central'
                    textAnchor='start'
                >
                    {label}
                </text>
            );
        };

        if (typeof getChartSelector === 'function') {
            this.props.getChartSelector(this.renderPieSelect());
        }

        return <>
            {setTitle && setTitle((selectable && elemSelectPostition === 'title') ? this.renderPieSelect() : null)}
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <ResponsiveContainer aspect={aspect} width={width} height={height} id={this.props.id}>
                    <PieChart
                        style={pieChartStyle}
                        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    >
                        <Pie
                            startAngle={90} // startet um 12 Uhr
                            endAngle={-270}
                            data={data}
                            dataKey='value'
                            labelLine={false}
                            label={renderCustomLabel}
                            isAnimationActive={false}
                            outerRadius={pieChartOuterRadius}
                            fill='#8884d8'
                        >
                            {data.map((entry, idx) =>
                                <Cell
                                    key={`cell-${idx}`}
                                    fill={data[idx].color ? data[idx].color : fillColor[idx % fillColor.length]}
                                />
                            )}
                        </Pie>
                        <Tooltip
                            content={((contentData: any) =>
                                <PieChartTooltip
                                    data={contentData}
                                    legendTitle={this.props.legendTitle}
                                    unit={this.props.unit}
                                />
                            )}
                            wrapperStyle={{
                                color: '#888',
                                backgroundColor: 'whitesmoke',
                                border: '1px solid #ccc',
                                padding: 5,
                                opacity: 0.9
                            }}
                        />
                        {!this.props.hideLegend &&
                            <Legend
                                align='left'
                                verticalAlign='top'
                                iconSize={10}
                                iconType='square' // 'line' | 'plainline' | 'square' | 'rect' | 'circle' | 'cross' | 'diamond' | 'star' | 'triangle' | 'wye'
                                formatter={this.legendFormatter}
                                wrapperStyle={pieChartLegendStyle}
                            />
                        }
                    </PieChart>
                </ResponsiveContainer>
                {selectable && !getChartSelector && elemSelectPostition === 'chart' && this.renderPieSelect()}
            </div>
        </>;
    }

    renderPieSelect(): JSX.Element {
        const { selectable, onSelectChange, elemSelectPostition } = this.props;
        const { selected } = this.state;
        let divClass: string = null;
        let divStyle: any = {
            top: 0,
            right: 10,
            position: 'absolute'
        }
        if (elemSelectPostition === 'title') {
            divClass = style.formCompact;
            divStyle = null;
        }
        if (this.props.getChartSelector) {
            divStyle = null;
        }
        return (
            <div className={divClass} style={divStyle}>
                <select
                    style={{ backgroundColor: 'white' }}
                    value={selected}
                    onChange={(evt: any) => {
                        const key = evt.target.value;
                        this.setState({ selected: key });

                        if (onSelectChange) {
                            onSelectChange(key);
                        }
                    }}
                >
                    {Object.keys(selectable).map((k: string, idx: number) =>
                        <option key={`option-${idx}`} value={k}>{k}</option>
                    )}
                </select>
            </div>
        );
    }
}
